// Chadwick voice lines — fallback step audio + idle chit-chat for
// cooking sessions. Chadwick is one *voice* in the narrator system,
// not its own playback engine. All actual audio output goes through
// playEngineNarration (BoodaVoice → AVAudioEngine on iOS native, or
// HTML5 <audio> on web). The only Chadwick-specific things in this
// file are: the bundled MP3 pools, the per-session step-line dedupe,
// and the idle chit-chat scheduler.
//
// History: this file used to have its own Web Audio context, decoded
// buffer cache, gain-ramp fade machinery, and parallel
// audioFloor.claim/release coordination. All of that is now done in
// engineNarration on iOS (which uses AVAudioPlayerNode + VPIO so the
// recognizer doesn't hear playback) and via HTML5 <audio> on web.
// The merge unifies the bug surface: a Chadwick race is the same
// race per-step narration would hit, fixed by the same retry logic.

import {
  claim as claimFloor,
  release as releaseFloor,
  extend as extendFloor,
} from "./audioFloor.js";
import { playEngineNarration, stopEngineNarration, engineNarrationSupported } from "./engineNarration.js";

// ── Pools ────────────────────────────────────────────────────────
const STEP_LINES = {
  1:  ["/mp3/chadwick-cooking/steps/step-1/step-1.mp3"],
  2:  ["/mp3/chadwick-cooking/steps/step-2/step-2.mp3"],
  3:  ["/mp3/chadwick-cooking/steps/step-3/step-3.mp3"],
  4:  ["/mp3/chadwick-cooking/steps/step-4/step-4.mp3"],
  5:  ["/mp3/chadwick-cooking/steps/step-5/step-5.mp3"],
  6:  ["/mp3/chadwick-cooking/steps/step-6/step-6.mp3"],
  7:  ["/mp3/chadwick-cooking/steps/step-7/step-7.mp3"],
  8:  ["/mp3/chadwick-cooking/steps/step-8/step-8.mp3"],
  9:  ["/mp3/chadwick-cooking/steps/step-9/step-9.mp3"],
  10: ["/mp3/chadwick-cooking/steps/step-10/step-10.mp3"],
};

const FINAL_PLATES = [
  "/mp3/chadwick-cooking/final-plates/final-plate-1.mp3",
  "/mp3/chadwick-cooking/final-plates/final-plate-2.mp3",
  "/mp3/chadwick-cooking/final-plates/final-plate-3.mp3",
  "/mp3/chadwick-cooking/final-plates/final-plate-4.mp3",
  "/mp3/chadwick-cooking/final-plates/final-plate-5.mp3",
  "/mp3/chadwick-cooking/final-plates/final-plate-6.mp3",
  "/mp3/chadwick-cooking/final-plates/final-plate-7.mp3",
  "/mp3/chadwick-cooking/final-plates/final-plate-italian-1.mp3",
  "/mp3/chadwick-cooking/final-plates/final-plate-italian-2.mp3",
];

const IDLE_LINES = [
  "/mp3/chadwick-cooking/idle-state/idle-chit-chat-1.mp3",
  "/mp3/chadwick-cooking/idle-state/idle-chit-chat-2.mp3",
  "/mp3/chadwick-cooking/idle-state/idle-chit-chat-3.mp3",
  "/mp3/chadwick-cooking/idle-state/idle-chit-chat-4.mp3",
  "/mp3/chadwick-cooking/idle-state/idle-chit-chat-5.mp3",
  "/mp3/chadwick-cooking/idle-state/idle-chit-chat-6.mp3",
];

const MINIMIZE_LINES = [
  "/mp3/chadwick-cooking/banner-minimized/minimze-cooking-1.mp3",
  "/mp3/chadwick-cooking/banner-minimized/minimize-cooking-2.mp3",
  "/mp3/chadwick-cooking/banner-minimized/minimize-cooking-3.mp3",
];

// ── Idle scheduler cadence ───────────────────────────────────────
const FIRST_IDLE_DELAY_MS        = 20_000;
const FIRST_IDLE_RESUME_DELAY_MS = 8_000;
const MIN_IDLE_INTERVAL_MS       = 90 * 1000;
const MAX_IDLE_INTERVAL_MS       = 180 * 1000;

// ── Active-clip tracking ─────────────────────────────────────────
// "step" wins over "idle" — when a step starts, an in-flight idle
// is interrupted; an idle scheduled while a step plays defers.
let currentKind = null;             // "step" | "idle" | null
// HTML5 <audio> element when running on the web fallback path.
// Tracked here so a supersede can pause it; iOS supersedes are
// handled inside playEngineNarration / Swift.
let webActiveAudio = null;

// ── Floor coordination ──────────────────────────────────────────
// One claim per playback. Refcounted in audioFloor.js so overlapping
// playback (TTS + voiceline) doesn't release each other's floor
// early. The music ducker (BackgroundMusic.jsx) listens to
// chadwick:speak-start/-end for ducking.
let activeClaimed = false;
function emitStart(ttlMs = 60_000) {
  if (activeClaimed) {
    extendFloor(ttlMs);
    return;
  }
  activeClaimed = true;
  claimFloor(ttlMs);
}
function emitEnd() {
  if (!activeClaimed) return;
  activeClaimed = false;
  releaseFloor();
}

// ── Single playback path ────────────────────────────────────────
// iOS: through engine → narrationPlayer → mainMixer → outputNode.
// VPIO subtracts this from mic input so the recognizer doesn't hear
// Booda's own voice. The retry logic in playEngineNarration handles
// the engine-not-yet-started race on banner-resume / fresh mount.
//
// Web: HTML5 <audio>. Browsers handle echo cancellation on the
// getUserMedia stream that Web Speech API uses, so chime/narration
// playback through <audio> doesn't feed back into recognition.
async function playUrl(url, kind = "step") {
  if (!url) return false;
  // Idle-vs-step gate: a step always wins. An idle scheduled while
  // a step is playing defers to the next slot.
  if (kind === "idle" && currentKind === "step") return false;

  // Stop any prior web audio cleanly. iOS supersedes are handled by
  // playEngineNarration's internal supersede logic.
  if (webActiveAudio) {
    try { webActiveAudio.pause(); } catch { /* ignore */ }
    try { webActiveAudio.src = ""; } catch { /* ignore */ }
    webActiveAudio = null;
  }

  currentKind = kind;
  emitStart(60_000);

  try {
    if (engineNarrationSupported()) {
      await playEngineNarration(url);
    } else {
      await playWebAudio(url);
    }
    if (currentKind === kind) currentKind = null;
    emitEnd();
    return true;
  } catch (err) {
    console.warn("[chadwick] playUrl failed:", err?.message || err);
    if (currentKind === kind) currentKind = null;
    emitEnd();
    return false;
  }
}

// HTML5 <audio> fallback for non-iOS. Resolves on natural end OR
// when superseded (paused by a follow-up call). Same supersede
// semantics as playEngineNarration.
function playWebAudio(url) {
  return new Promise((resolve, reject) => {
    if (typeof Audio === "undefined") {
      reject(new Error("HTML5 audio not available"));
      return;
    }
    const audio = new Audio(url);
    webActiveAudio = audio;
    audio.preload = "auto";
    const cleanup = () => {
      if (webActiveAudio === audio) webActiveAudio = null;
    };
    audio.addEventListener("ended", () => { cleanup(); resolve(); });
    audio.addEventListener("error", () => {
      cleanup();
      reject(new Error("audio playback failed"));
    });
    audio.play().catch((e) => {
      cleanup();
      reject(e);
    });
  });
}

function pickRandom(pool, lastIdx) {
  if (!Array.isArray(pool) || pool.length === 0) return { url: null, idx: -1 };
  if (pool.length === 1) return { url: pool[0], idx: 0 };
  let idx;
  do {
    idx = Math.floor(Math.random() * pool.length);
  } while (idx === lastIdx && pool.length > 1);
  return { url: pool[idx], idx };
}

// ── Step dedupe (one fire per step per cook session) ─────────────
const heardStepsBySession = new Map();

function playStepLine(stepIndex, totalSteps) {
  const isLast = stepIndex >= totalSteps - 1;
  if (isLast) {
    const { url } = pickRandom(FINAL_PLATES, -1);
    playUrl(url, "step");
    return;
  }
  const stepNumber = stepIndex + 1;
  const poolKey = ((stepNumber - 1) % Object.keys(STEP_LINES).length) + 1;
  const pool = STEP_LINES[poolKey];
  const { url } = pickRandom(pool, -1);
  playUrl(url, "step");
}

export function playChadwickStep(stepIndex, totalSteps, _recipe, sessionId) {
  if (!Number.isFinite(stepIndex) || !Number.isFinite(totalSteps)) return;
  if (stepIndex < 0 || totalSteps < 1) return;
  if (sessionId) {
    let heard = heardStepsBySession.get(sessionId);
    if (!heard) {
      heard = new Set();
      heardStepsBySession.set(sessionId, heard);
    }
    if (heard.has(stepIndex)) return;
    heard.add(stepIndex);
  }
  playStepLine(stepIndex, totalSteps);
}

// Replay bypasses the per-session dedup. Bound to the REPEAT voice
// command — the first play of a step is gated to one fire, but the
// user explicitly asking for it again should always work.
export function replayChadwickStep(stepIndex, totalSteps) {
  if (!Number.isFinite(stepIndex) || !Number.isFinite(totalSteps)) return;
  if (stepIndex < 0 || totalSteps < 1) return;
  playStepLine(stepIndex, totalSteps);
}

let lastMinimizeIdx = -1;
export function playChadwickMinimize() {
  const { url, idx } = pickRandom(MINIMIZE_LINES, lastMinimizeIdx);
  if (!url) return;
  lastMinimizeIdx = idx;
  playUrl(url, "step");
}

export function stopChadwickAudio() {
  stopIdleChitChat();
  if (engineNarrationSupported()) {
    stopEngineNarration().catch(() => {});
  }
  if (webActiveAudio) {
    try { webActiveAudio.pause(); } catch { /* ignore */ }
    try { webActiveAudio.src = ""; } catch { /* ignore */ }
    webActiveAudio = null;
  }
  if (currentKind) {
    currentKind = null;
    emitEnd();
  }
}

// ── Idle chit-chat scheduler ─────────────────────────────────────
let idleTimer  = null;
let idleActive = false;
let lastIdleIdx = -1;
let hasStartedOnce = false;

async function fireIdleChitChat() {
  if (!idleActive) return;
  if (currentKind === "step") {
    scheduleNextIdle();
    return;
  }
  const { url, idx } = pickRandom(IDLE_LINES, lastIdleIdx);
  if (!url) {
    scheduleNextIdle();
    return;
  }
  lastIdleIdx = idx;
  const started = await playUrl(url, "idle");
  if (!started) {
    scheduleNextIdle();
    return;
  }
  // playUrl awaits playEngineNarration which resolves on natural
  // end or supersede — by the time we reach here, the idle is done
  // and we can schedule the next slot.
  if (idleActive) scheduleNextIdle();
}

function scheduleNextIdle() {
  if (!idleActive) return;
  if (idleTimer) clearTimeout(idleTimer);
  const interval = MIN_IDLE_INTERVAL_MS
    + Math.random() * (MAX_IDLE_INTERVAL_MS - MIN_IDLE_INTERVAL_MS);
  idleTimer = setTimeout(fireIdleChitChat, interval);
}

export function startIdleChitChat() {
  if (idleActive) return;
  idleActive = true;
  if (idleTimer) clearTimeout(idleTimer);
  const delay = hasStartedOnce ? FIRST_IDLE_RESUME_DELAY_MS : FIRST_IDLE_DELAY_MS;
  hasStartedOnce = true;
  idleTimer = setTimeout(fireIdleChitChat, delay);
}

export function stopIdleChitChat() {
  idleActive = false;
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  if (currentKind === "idle") {
    if (engineNarrationSupported()) {
      stopEngineNarration().catch(() => {});
    } else if (webActiveAudio) {
      try { webActiveAudio.pause(); } catch { /* ignore */ }
      try { webActiveAudio.src = ""; } catch { /* ignore */ }
      webActiveAudio = null;
    }
    currentKind = null;
    emitEnd();
  }
}
